import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mountain, Loader2, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * True when the failure looks like a transient network hiccup that a
 * silent retry can recover from — cold backend, TLS handshake blip, or
 * an in-flight service-worker activate. We explicitly refuse to retry
 * meaningful HTTP responses like 401 (bad password) or 429 (rate limit)
 * so we never lock a real user out with duplicate attempts.
 */
function isRetriableAuthError(error) {
  if (!error) return false;
  const status = error?.response?.status;
  if (status) return false;                 // any HTTP response means the network worked
  if (error?.code === "ERR_NETWORK") return true;
  if (error?.code === "ECONNABORTED") return true;
  if (typeof error?.message === "string" && error.message.toLowerCase().includes("network")) return true;
  if (typeof error?.message === "string" && error.message.toLowerCase().includes("timeout")) return true;
  return false;
}

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [storeName, setStoreName] = useState("");
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const attemptAuth = async () => {
    if (isRegister) {
      await register(username, password, storeName || null);
    } else {
      await login(username, password);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    if (isRegister && password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }

    setLoading(true);
    try {
      try {
        await attemptAuth();
      } catch (firstError) {
        // Silent 1-shot retry ONLY for transient network failures. This is
        // what makes the first login after a deploy work: the service
        // worker's activate can briefly race with the auth request; a
        // ~800ms retry side-steps the race without user intervention.
        if (isRetriableAuthError(firstError)) {
          setRetrying(true);
          await new Promise((r) => setTimeout(r, 800));
          try {
            await attemptAuth();
          } finally {
            setRetrying(false);
          }
        } else {
          throw firstError;
        }
      }
      toast.success(isRegister
        ? "¡Cuenta creada! Bienvenido a tu período de prueba de 14 días."
        : "Bienvenido a SkiFlow Rental");
      navigate("/");
    } catch (error) {
      // Log full error to console for diagnostics
      console.error("[Login] Error:", error, "response:", error?.response, "message:", error?.message);

      const backendDetail = error?.response?.data?.detail;
      const status = error?.response?.status;
      let message;

      if (status === 401 || backendDetail === "Invalid credentials") {
        message = "Usuario o contraseña incorrectos";
      } else if (status === 429) {
        message = "Demasiados intentos. Espera unos segundos y vuelve a intentarlo.";
      } else if (backendDetail) {
        message = backendDetail;
      } else if (status) {
        message = `Error ${status}: ${error.response.statusText || "Error del servidor"}`;
      } else if (isRetriableAuthError(error)) {
        // Retry already exhausted — surface a friendly network message
        // without touching the service worker or local storage.
        message = "No se pudo conectar. Comprueba tu conexión e inténtalo de nuevo.";
      } else {
        message = error?.message || "Error de autenticación. Abre la consola (F12) para más detalles.";
      }
      toast.error(message);
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Introduce tu email");
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail }, {
        headers: { "X-Forgot-Email": forgotEmail }
      });
      setForgotSent(true);
    } catch {
      toast.error("Error al enviar el email. Inténtalo de nuevo.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Back button */}
      <div className="p-4 absolute top-0 left-0 z-50">
        <Button 
          variant="ghost" 
          onClick={() => {
            console.log('Navigating to landing...');
            navigate("/landing");
          }}
          className="gap-2 text-white bg-black/40 hover:bg-black/60 backdrop-blur-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver al inicio
        </Button>
      </div>
      
      <div className="flex-1 flex">
        {/* Left - Diferente para Login vs Register */}
        {isRegister ? (
          // REGISTRO: Degradado con copy y features
          <div 
            className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
            style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)'
            }}
          >
            <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
              {/* Logo y título arriba */}
              <div>
                <img src="/logo-white.png" alt="SkiFlow Rental" className="h-16 w-auto object-contain mb-8" />
                <h1 className="text-5xl font-black mb-4 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                  Despacha en<br/>
                  <span className="italic">segundos</span>,<br/>
                  no en minutos.
                </h1>
                <p className="text-xl text-white/90 leading-relaxed max-w-md">
                  El software de alquiler que elimina las colas y recupera el control de tu stock en tiempo real.
                </p>
              </div>
              
              {/* Features abajo */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-white/90">14 días de prueba gratis</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-white/90">Sin tarjeta de crédito</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-white/90">Cancela cuando quieras</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // LOGIN: Solo logo + imagen potente de nieve
          <div 
            className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
            style={{ 
              backgroundImage: "url('https://images.unsplash.com/photo-1769774506254-4be19bf63a23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxza2lpbmclMjBhY3Rpb24lMjBzbm93JTIwbW91bnRhaW4lMjBwb3dkZXJ8ZW58MHx8fHwxNzcxMDY3ODE4fDA&ixlib=rb-4.1.0&q=85')"
            }}
          >
            <div className="absolute inset-0 bg-black/30" />
            <div className="relative z-10 flex flex-col justify-center items-center w-full">
              <img src="/logo-white.png" alt="SkiFlow Rental" className="h-20 w-auto object-contain" />
            </div>
          </div>
        )}

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4 lg:hidden">
              <img src="/logo.png" alt="SkiFlow Rental" className="h-12 w-auto object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {isRegister ? "Crear cuenta" : "Iniciar sesión"}
            </CardTitle>
            <CardDescription>
              {isRegister 
                ? "Crea tu cuenta para acceder al sistema" 
                : "Ingresa tus credenciales para continuar"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* ── FORGOT PASSWORD VIEW ── */}
            {showForgot ? (
              <div className="space-y-4">
                {forgotSent ? (
                  <div className="text-center space-y-4 py-4">
                    <CheckCircle className="h-14 w-14 text-green-500 mx-auto" />
                    <div>
                      <p className="font-semibold text-base">Email enviado</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Si tu email está registrado, recibirás un enlace de recuperación en breve. Revisa también tu carpeta de spam.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(""); }}
                      data-testid="back-to-login-btn"
                    >
                      Volver al login
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-muted-foreground pb-1">
                      Introduce el email de tu cuenta y te enviaremos un enlace para restablecer tu contraseña.
                    </div>
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="forgot-email">Email</Label>
                        <Input
                          id="forgot-email"
                          type="email"
                          placeholder="tu@email.com"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="h-11"
                          data-testid="forgot-email-input"
                          autoFocus
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full h-11 font-semibold bg-black hover:bg-black/90 text-white"
                        disabled={forgotLoading}
                        data-testid="forgot-submit-btn"
                      >
                        {forgotLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Enviar enlace de recuperación
                      </Button>
                    </form>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => { setShowForgot(false); setForgotEmail(""); }}
                        className="text-sm text-muted-foreground hover:text-foreground underline"
                      >
                        Volver al login
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
            /* ── LOGIN / REGISTER FORM ── */
            <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">{isRegister ? "Email" : "Usuario"}</Label>
                <Input
                  id="username"
                  type={isRegister ? "email" : "text"}
                  placeholder={isRegister ? "tu@email.com" : "Tu nombre de usuario"}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="h-11"
                  data-testid="username-input"
                  tabIndex={1}
                  autoFocus
                />
              </div>
              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="storeName">Nombre de tu tienda (opcional)</Label>
                  <Input
                    id="storeName"
                    type="text"
                    placeholder="Ej: Ski Rental Madrid"
                    value={storeName}
                    onChange={(e) => setStoreName(e.target.value)}
                    className="h-11"
                    tabIndex={2}
                  />
                </div>
              )}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Contraseña</Label>
                  {!isRegister && (
                    <button
                      type="button"
                      onClick={() => setShowForgot(true)}
                      className="text-xs text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder={isRegister ? "Al menos 8 caracteres" : "Tu contraseña"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                  data-testid="password-input"
                  tabIndex={3}
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 font-semibold bg-black hover:bg-black/90 text-white"
                disabled={loading}
                data-testid="login-submit-btn"
                tabIndex={4}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {retrying
                  ? "Conectando..."
                  : loading
                    ? (isRegister ? "Creando..." : "Entrando...")
                    : (isRegister ? "Crear cuenta" : "Iniciar sesión")}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <Link
                to="/register"
                className="text-sm text-primary hover:underline"
                data-testid="toggle-auth-mode"
              >
                ¿No tienes cuenta? Regístrate gratis
              </Link>
            </div>
            </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </div>
  );
}
